import useFlexBoxStyles from '../common/flexBox';
import MicroFront from './micro-front/MicroFront';
import useColorsStyles from '../common/colors';
import { useDispatch, useSelector } from 'react-redux';
import { getShowingBackgroundSlug, setShowingBackgroundSlug } from '../redux/slices/appSlice';
import { IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getLicense } from '../redux/slices/licenseSlice';
import { getUser } from '../redux/slices/authSlice';
import UserRole from '../common/enums/roles';
import OlTree from '@onlive.site/ui/dist/react/tree';
import OlTreeItem from '@onlive.site/ui/dist/react/tree-item';
import MatchIcon from '@mui/icons-material/CheckBox';
import MismatchIcon from '@mui/icons-material/DisabledByDefault';
import Divider from '@mui/material/Divider';
import { useEffect, useMemo } from 'react';
import { updateMicroFrontBackground } from '../redux/slices/organizationSlice';
import ActivatedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import DeactivatedIcon from '@mui/icons-material/ArrowCircleDownOutlined';

const DefaultBackgroundPath = '/background';

const isSimpleValue = (value) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined;

const match = (value, target) => {
    if (isSimpleValue(value)) {
        return value === target;
    }
    if (!target) {
        return false;
    }
    if (Array.isArray(value)) {
        if (Array.isArray(target)) {
            return value.every((v) => target.find((t) => match(v, t)));
        }
        return false;
    } else if (Array.isArray(target)) {
        return false;
    }
    for (const [key, v] of Object.entries(value)) {
        if (!match(v, target[key])) {
            return false;
        }
    }
    return true;
}

const renderMatchTreeItems = (value, target, flexBox, colors, cx) => {
    if (isSimpleValue(value)) {
        let matched = match(value, target);
        let item = (
            <div className={cx(flexBox.flex, flexBox.alignItemsCenter, flexBox.gap1)}>
                :
                <div className={cx(flexBox.flex, flexBox.alignItemsCenter, flexBox.gap1, matched ? colors.textSuccess : colors.textError)}>
                    {String(value)}
                </div>
            </div>
        );
        if (!matched) {
            item = (
                <Tooltip title={`expected: ${JSON.stringify(target)}`}>
                    {item}
                </Tooltip>
            );
        }
        return [matched, item];
    }
    
    if (Array.isArray(value)) {
        if (Array.isArray(target)) {
            let matched = false;
            const items = value.map((v, index) => {
                let subMatch = false;
                let item = null;
                for (let i = 0; i < target.length; i++) {
                    if (match(v, target[i])) {
                        subMatch = true;
                        item = renderMatchTreeItems(v, target[i], flexBox, colors, cx)[1];
                        break;
                    }
                }
                matched = matched || subMatch;
                return (
                    <OlTreeItem key={`array-item-${index}`} expanded={!subMatch}>
                        #{index} {subMatch ? <MatchIcon className={colors.textSuccess}/> : <MismatchIcon/>}
                        {item || (
                            <Tooltip title={`No match found in ${JSON.stringify(target)}`}>
                                <div className={cx(flexBox.flex, flexBox.alignItemsCenter, flexBox.gap1)}>
                                    :
                                    <div className={cx(flexBox.flex, flexBox.alignItemsCenter, flexBox.gap1)}>
                                        {JSON.stringify(v)}
                                    </div>
                                </div>
                            </Tooltip>
                        )}
                    </OlTreeItem>
                );
            });
            return [matched, items];
        }
        
        return [false,(
            <Tooltip title={JSON.stringify(value)}>
                <div>
                    Unexpected array!
                </div>
            </Tooltip>
        )];
    }
    
    if (isSimpleValue(target) || Array.isArray(target)) {
        return [false, (
            <Tooltip title={JSON.stringify(value, null, 2)}>
                <div>
                    Unexpected object!
                </div>
            </Tooltip>
        )];
    }
    
    let matched = true;
    const items = Object.entries(value).map(([key, v]) => {
        const [subMatched, subItems] = renderMatchTreeItems(v, target[key], flexBox, colors, cx);
        matched = matched && subMatched;
        return (
            <OlTreeItem key={`object-item-${key}`} expanded={!subMatched}>
                {key} {subMatched ? <MatchIcon className={colors.textSuccess}/> : <MismatchIcon className={colors.textError}/>}
                {subItems}
            </OlTreeItem>
        );
    });
    
    return [matched, items];
}

const MatchTree = ({ value, target, className }) => {
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();

    return (
        <OlTree className={className}>
            {renderMatchTreeItems(value, target, flexBox, colors, cx)}
        </OlTree>
    );
}

export default function BackgroundMicroFront({ slug, config }) {
    const showingBackgroundSlug = useSelector(getShowingBackgroundSlug);
    const license = useSelector(getLicense);
    const user = useSelector(getUser);
    const role = user?.role;
    
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    
    const dispatch = useDispatch();
    
    const { path, license: configLicense, roles: configRoles } = config;
    
    const backgroundLicense = useMemo(() => configLicense || {}, [configLicense]);
    const backgroundRoles = useMemo(() => {
        const roles = Array.isArray(configRoles) ? [...configRoles] : [];
        if (role === UserRole.ROOT) {
            if (!roles.includes(UserRole.ADMIN)) {
                roles.push(UserRole.ADMIN);
            }
            if (!roles.includes(UserRole.ROOT)) {
                roles.push(UserRole.ROOT);
            }
        }
        if (role === UserRole.ADMIN && !roles.includes(UserRole.ADMIN)) {
            roles.push(UserRole.ADMIN);
        }
        return roles;
    }, [configRoles, role]);
    
    const roles = useMemo(() => {
        const roles = [role];
        if (role === UserRole.ROOT) {
            if (!roles.includes(UserRole.ADMIN)) {
                roles.push(UserRole.ADMIN);
            }
            if (!roles.includes(UserRole.ROOT)) {
                roles.push(UserRole.ROOT);
            }
        }
        if (role === UserRole.ADMIN && !roles.includes(UserRole.ADMIN)) {
            roles.push(UserRole.ADMIN);
        }
        return roles;
    }, [role]);
    
    const activated = useMemo(
        () => match(backgroundLicense, license) && match(backgroundRoles, roles),
        [backgroundLicense, backgroundRoles, license, roles],
    );
    
    const [matchValue, matchTarget] = useMemo(() => [{
        license: backgroundLicense,
        roles: backgroundRoles,
    }, {
        license,
        roles,
    }], [backgroundLicense, backgroundRoles, license, roles]);

    useEffect(() => {
        dispatch(updateMicroFrontBackground(slug, { activated }));
    }, [dispatch, slug, activated]);
    
    return (
        <div className={cx(
            flexBox.absolute,
            flexBox.w100,
            flexBox.h100,
            flexBox.left0,
            flexBox.top0,
            flexBox.flex,
            flexBox.column,
            flexBox.overflowHidden,
            colors.bgWhite,
            showingBackgroundSlug !== slug && flexBox.displayNone,
            )}
        >
            <div className={cx(flexBox.flex, flexBox.alignItemsCenter, flexBox.gap2, flexBox.padding2)}>
                <Typography
                    variant="h6"
                    className={cx(
                        flexBox.paddingLeft1,
                        flexBox.paddingRight1,
                        flexBox.borderRadius1,
                        colors.bgLightGray,
                    )}
                >
                    {slug}
                </Typography>
                {activated ? (
                    <ActivatedIcon className={colors.textSuccess} />
                ) : (
                    <DeactivatedIcon className={colors.textError} />
                )}
                <div className={flexBox.grow1}/>
                <IconButton onClick={() => dispatch(setShowingBackgroundSlug(null))}>
                    <CloseIcon />
                </IconButton>
            </div>
            <Divider />
            <div className={cx(flexBox.flex, flexBox.grow1)}>
                <div
                    className={cx(
                        flexBox.flex,
                        flexBox.column,
                        flexBox.w30,
                        flexBox.h100,
                        flexBox.overflowAuto,
                        flexBox.scrollbarNone,
                        flexBox.padding2,
                        flexBox.borderBox,
                    )}
                >
                    <MatchTree value={matchValue} target={matchTarget} className={cx(flexBox.w100, flexBox.h100)} />
                </div>
                <Divider orientation="vertical" />
                <div
                    className={cx(
                        flexBox.flex,
                        flexBox.alignItemsCenter,
                        flexBox.justifyContentCenter,
                        flexBox.grow1,
                        colors.bgSlightlyGray,
                    )}
                >
                    {activated
                        ? <MicroFront slug={slug} path={path || DefaultBackgroundPath} />
                        : <DeactivatedIcon className={colors.textError} />}
                </div>
            </div>
        </div>
    );
}